import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import PartnerLayout from "./PartnerLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Heart,
  Calendar,
  TrendingUp,
  Repeat,
  Sparkles,
  ArrowLeft,
  ArrowRight,
  Play,
  Pause,
  Square,
  Trash2,
  Target,
  DollarSign,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Ticket,
  Gift,
  MessageSquare,
  ChevronRight,
  Zap,
} from "lucide-react";
import type { CampaignTemplateType } from "@shared/schema";
import type { CampaignTemplate, CampaignConfigField } from "@shared/campaignTemplates";

interface Campaign {
  id: string;
  restaurantId: string;
  eatzyBusinessId: string;
  templateType: CampaignTemplateType;
  name: string;
  status: string;
  budgetCents: number | null;
  spentCents: number;
  startDate: string | null;
  endDate: string | null;
  templateConfig: Record<string, any>;
  stepsSnapshot: any[];
  metrics: Record<string, number>;
  createdAt: string;
  updatedAt: string;
}

const ICON_MAP: Record<string, typeof Heart> = {
  Heart, Calendar, TrendingUp, Repeat, Sparkles,
};

const ACTION_ICONS: Record<string, typeof Ticket> = {
  coupon: Ticket,
  kudoz: Gift,
  message: MessageSquare,
};

type View = 'dashboard' | 'select' | 'setup';

export default function PartnerCampaigns() {
  const { toast } = useToast();
  const restaurantId = localStorage.getItem('partnerRestaurantId') || '';
  const restaurantName = localStorage.getItem('partnerRestaurantName') || '';
  const eatzyToken = sessionStorage.getItem('partnerEatzyToken');
  const businessId = localStorage.getItem('partnerRestaurantProfile')
    ? (() => { try { return JSON.parse(localStorage.getItem('partnerRestaurantProfile')!).business_id; } catch { return ''; } })()
    : '';

  const [view, setView] = useState<View>('dashboard');
  const [selectedTemplate, setSelectedTemplate] = useState<CampaignTemplate | null>(null);
  const [configValues, setConfigValues] = useState<Record<string, any>>({});
  const [campaignName, setCampaignName] = useState('');
  const [budgetEnabled, setBudgetEnabled] = useState(false);
  const [budgetAmount, setBudgetAmount] = useState('');
  const [durationDays, setDurationDays] = useState('30');
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; campaignId: string; action: string }>({ open: false, campaignId: '', action: '' });

  const { data: templates, isLoading: templatesLoading } = useQuery<CampaignTemplate[]>({
    queryKey: ['/api/partner/campaigns/templates'],
    queryFn: async () => {
      const res = await fetch('/api/partner/campaigns/templates', {
        headers: { 'X-Eatzy-Token': eatzyToken || '' },
      });
      if (!res.ok) throw new Error('Failed to fetch templates');
      return res.json();
    },
    enabled: !!eatzyToken,
  });

  const { data: campaignsData, isLoading: campaignsLoading } = useQuery<Campaign[]>({
    queryKey: ['/api/partner/campaigns', restaurantId],
    queryFn: async () => {
      const res = await fetch(`/api/partner/campaigns/${restaurantId}`, {
        headers: { 'X-Eatzy-Token': eatzyToken || '' },
      });
      if (!res.ok) throw new Error('Failed to fetch campaigns');
      return res.json();
    },
    enabled: !!eatzyToken && !!restaurantId,
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await fetch('/api/partner/campaigns', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Eatzy-Token': eatzyToken || '',
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to create campaign');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/partner/campaigns', restaurantId] });
      toast({ title: "Campaign launched", description: "Your campaign is now active." });
      resetSetup();
      setView('dashboard');
    },
    onError: (err: any) => {
      toast({ title: "Failed to launch", description: err.message, variant: "destructive" });
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({ campaignId, status }: { campaignId: string; status: string }) => {
      const res = await fetch(`/api/partner/campaigns/${campaignId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-Eatzy-Token': eatzyToken || '',
        },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to update campaign');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/partner/campaigns', restaurantId] });
      toast({ title: "Campaign updated" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (campaignId: string) => {
      const res = await fetch(`/api/partner/campaigns/${campaignId}`, {
        method: 'DELETE',
        headers: { 'X-Eatzy-Token': eatzyToken || '' },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to delete campaign');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/partner/campaigns', restaurantId] });
      toast({ title: "Campaign deleted" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  function resetSetup() {
    setSelectedTemplate(null);
    setConfigValues({});
    setCampaignName('');
    setBudgetEnabled(false);
    setBudgetAmount('');
    setDurationDays('30');
  }

  function handleSelectTemplate(template: CampaignTemplate) {
    if (!template.available) return;
    setSelectedTemplate(template);
    const defaults: Record<string, any> = {};
    template.configFields.forEach(f => { defaults[f.key] = f.defaultValue; });
    setConfigValues(defaults);
    setCampaignName(template.name);
    setView('setup');
  }

  function handleLaunch() {
    if (!selectedTemplate || !campaignName.trim()) {
      toast({ title: "Missing info", description: "Please provide a campaign name.", variant: "destructive" });
      return;
    }

    const now = new Date();
    const endDate = new Date(now);
    endDate.setDate(endDate.getDate() + parseInt(durationDays));

    createMutation.mutate({
      restaurantId,
      eatzyBusinessId: businessId,
      templateType: selectedTemplate.type,
      name: campaignName.trim(),
      budgetCents: budgetEnabled && budgetAmount ? Math.round(parseFloat(budgetAmount) * 100) : null,
      startDate: now.toISOString(),
      endDate: endDate.toISOString(),
      templateConfig: configValues,
    });
  }

  function handleStatusAction(campaignId: string, action: string) {
    setConfirmDialog({ open: true, campaignId, action });
  }

  function confirmStatusAction() {
    const { campaignId, action } = confirmDialog;
    if (action === 'delete') {
      deleteMutation.mutate(campaignId);
    } else {
      statusMutation.mutate({ campaignId, status: action });
    }
    setConfirmDialog({ open: false, campaignId: '', action: '' });
  }

  function formatCents(cents: number) {
    return `$${(cents / 100).toFixed(0)}`;
  }

  function getStatusBadge(status: string) {
    const variants: Record<string, { variant: "default" | "secondary" | "outline" | "destructive"; label: string }> = {
      draft: { variant: 'secondary', label: 'Draft' },
      active: { variant: 'default', label: 'Active' },
      paused: { variant: 'outline', label: 'Paused' },
      completed: { variant: 'secondary', label: 'Completed' },
      expired: { variant: 'destructive', label: 'Expired' },
    };
    const v = variants[status] || variants.draft;
    return <Badge variant={v.variant}>{v.label}</Badge>;
  }

  const activeCampaigns = campaignsData?.filter(c => c.status === 'active') || [];
  const otherCampaigns = campaignsData?.filter(c => c.status !== 'active') || [];


  return (
    <PartnerLayout>
      <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6">
        {view === 'dashboard' && (
          <>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-2xl font-bold">Campaigns</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Automated campaigns for promotions, win-backs, and targeted offers
                </p>
              </div>
              <Button onClick={() => setView('select')} data-testid="button-new-campaign">
                <Zap className="w-4 h-4 mr-2" />
                New Campaign
              </Button>
            </div>

            {campaignsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : !campaignsData || campaignsData.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                  <Target className="w-12 h-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No campaigns yet</h3>
                  <p className="text-sm text-muted-foreground mb-6 max-w-md">
                    Campaigns run automatically based on rules you set — win-backs, slow-day promotions, loyalty milestones, and more.
                  </p>
                  <Button onClick={() => setView('select')}>
                    <Zap className="w-4 h-4 mr-2" />
                    Create Campaign
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {activeCampaigns.length > 0 && (
                  <div className="space-y-3">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                      <Play className="w-4 h-4 text-green-500" />
                      Active Campaigns
                    </h2>
                    <div className="grid gap-4">
                      {activeCampaigns.map(campaign => (
                        <CampaignCard
                          key={campaign.id}
                          campaign={campaign}
                          templates={templates || []}
                          onStatusAction={handleStatusAction}
                          formatCents={formatCents}
                          getStatusBadge={getStatusBadge}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {otherCampaigns.length > 0 && (
                  <div className="space-y-3">
                    <h2 className="text-lg font-semibold text-muted-foreground">
                      Past & Paused Campaigns
                    </h2>
                    <div className="grid gap-4">
                      {otherCampaigns.map(campaign => (
                        <CampaignCard
                          key={campaign.id}
                          campaign={campaign}
                          templates={templates || []}
                          onStatusAction={handleStatusAction}
                          formatCents={formatCents}
                          getStatusBadge={getStatusBadge}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

          </>
        )}

        {view === 'select' && (
          <>
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => setView('dashboard')} data-testid="button-back">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Choose a Campaign Objective</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Select the goal you want to achieve — we'll handle the rest
                </p>
              </div>
            </div>

            {templatesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {templates?.map(template => {
                  const Icon = ICON_MAP[template.icon] || Target;
                  return (
                    <Card
                      key={template.type}
                      className={`cursor-pointer transition-all ${
                        template.available
                          ? 'hover-elevate'
                          : 'opacity-50 cursor-not-allowed'
                      }`}
                      onClick={() => handleSelectTemplate(template)}
                      data-testid={`template-${template.type}`}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between gap-2">
                          <div
                            className="p-2 rounded-md"
                            style={{ backgroundColor: `${template.color}15` }}
                          >
                            <Icon className="w-5 h-5" style={{ color: template.color }} />
                          </div>
                          {!template.available && (
                            <Badge variant="outline" className="text-xs">Coming Soon</Badge>
                          )}
                        </div>
                        <CardTitle className="text-base mt-2">{template.name}</CardTitle>
                        <CardDescription className="text-xs">{template.tagline}</CardDescription>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <p className="text-sm text-muted-foreground">{template.description}</p>
                        {template.available && template.steps.length > 0 && (
                          <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            <span>{template.estimatedDuration}</span>
                            <span className="mx-1">·</span>
                            <span>{template.steps.length} automated steps</span>
                          </div>
                        )}
                      </CardContent>
                      {template.available && (
                        <CardFooter className="pt-0">
                          <Button variant="ghost" size="sm" className="ml-auto gap-1">
                            Set up <ChevronRight className="w-3 h-3" />
                          </Button>
                        </CardFooter>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}

        {view === 'setup' && selectedTemplate && (
          <>
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => { resetSetup(); setView('select'); }} data-testid="button-back-setup">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">{selectedTemplate.name}</h1>
                <p className="text-sm text-muted-foreground mt-1">{selectedTemplate.tagline}</p>
              </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-5">
              <div className="lg:col-span-3 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Campaign Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="campaignName">Campaign name</Label>
                      <Input
                        id="campaignName"
                        value={campaignName}
                        onChange={e => setCampaignName(e.target.value)}
                        placeholder="e.g., Spring Loyalty Drive"
                        data-testid="input-campaign-name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="duration">Duration</Label>
                      <Select value={durationDays} onValueChange={setDurationDays}>
                        <SelectTrigger data-testid="select-duration">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="7">7 days</SelectItem>
                          <SelectItem value="14">14 days</SelectItem>
                          <SelectItem value="30">30 days</SelectItem>
                          <SelectItem value="60">60 days</SelectItem>
                          <SelectItem value="90">90 days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Separator />

                    <div className="flex items-center justify-between gap-4">
                      <div className="space-y-0.5">
                        <Label>Set a budget limit</Label>
                        <p className="text-xs text-muted-foreground">
                          Cap the total value of incentives given away
                        </p>
                      </div>
                      <Switch
                        checked={budgetEnabled}
                        onCheckedChange={setBudgetEnabled}
                        data-testid="toggle-budget"
                      />
                    </div>

                    {budgetEnabled && (
                      <div className="space-y-2">
                        <Label htmlFor="budget">Maximum budget</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="budget"
                            type="number"
                            min="0"
                            step="10"
                            value={budgetAmount}
                            onChange={e => setBudgetAmount(e.target.value)}
                            placeholder={`${selectedTemplate.suggestedBudget.min / 100} - ${selectedTemplate.suggestedBudget.max / 100}`}
                            className="pl-9"
                            data-testid="input-budget"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Suggested: ${selectedTemplate.suggestedBudget.min / 100} – ${selectedTemplate.suggestedBudget.max / 100}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {selectedTemplate.configFields.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Campaign Settings</CardTitle>
                      <CardDescription>Customize the campaign to match your needs</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {selectedTemplate.configFields.map(field => (
                        <ConfigFieldInput
                          key={field.key}
                          field={field}
                          value={configValues[field.key]}
                          onChange={(val) => setConfigValues(prev => ({ ...prev, [field.key]: val }))}
                        />
                      ))}
                    </CardContent>
                  </Card>
                )}

                <div className="flex items-center justify-between gap-4">
                  <Button variant="outline" onClick={() => { resetSetup(); setView('select'); }}>
                    Back
                  </Button>
                  <Button
                    onClick={handleLaunch}
                    disabled={createMutation.isPending || !campaignName.trim()}
                    data-testid="button-launch"
                  >
                    {createMutation.isPending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Launch Campaign
                  </Button>
                </div>
              </div>

              <div className="lg:col-span-2 space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Zap className="w-4 h-4" />
                      Campaign Plan
                    </CardTitle>
                    <CardDescription>What happens when you launch</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-0">
                      {selectedTemplate.steps.map((step, i) => {
                        const ActionIcon = ACTION_ICONS[step.action.type] || MessageSquare;
                        return (
                          <div key={step.stepNumber} className="relative">
                            {i < selectedTemplate.steps.length - 1 && (
                              <div className="absolute left-[15px] top-[32px] bottom-0 w-px bg-border" />
                            )}
                            <div className="flex gap-3 pb-6">
                              <div
                                className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                                style={{ backgroundColor: selectedTemplate.color }}
                              >
                                {step.stepNumber}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm">{step.name}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{step.description}</p>
                                <div className="flex items-center gap-1.5 mt-2">
                                  <ActionIcon className="w-3 h-3 text-muted-foreground" />
                                  <span className="text-xs text-muted-foreground">{step.action.label}</span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1 italic">{step.triggerDescription}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </>
        )}

        <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, campaignId: '', action: '' })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {confirmDialog.action === 'paused' && 'Pause Campaign'}
                {confirmDialog.action === 'active' && 'Resume Campaign'}
                {confirmDialog.action === 'completed' && 'Complete Campaign'}
                {confirmDialog.action === 'delete' && 'Delete Campaign'}
              </DialogTitle>
              <DialogDescription>
                {confirmDialog.action === 'paused' && 'Pausing will temporarily stop all automated actions. You can resume anytime.'}
                {confirmDialog.action === 'active' && 'This will resume all automated campaign actions.'}
                {confirmDialog.action === 'completed' && 'Completing this campaign will stop all actions permanently. This cannot be undone.'}
                {confirmDialog.action === 'delete' && 'This will permanently remove the campaign and all its data. This cannot be undone.'}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDialog({ open: false, campaignId: '', action: '' })}>
                Cancel
              </Button>
              <Button
                variant={confirmDialog.action === 'delete' ? 'destructive' : 'default'}
                onClick={confirmStatusAction}
              >
                Confirm
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PartnerLayout>
  );
}

function ConfigFieldInput({ field, value, onChange }: { field: CampaignConfigField; value: any; onChange: (val: any) => void }) {
  if (field.type === 'number') {
    return (
      <div className="space-y-2">
        <Label>{field.label}</Label>
        <div className="relative">
          {field.unit === '$' && (
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          )}
          <Input
            type="number"
            value={value ?? ''}
            onChange={e => onChange(parseFloat(e.target.value) || 0)}
            className={field.unit === '$' ? 'pl-9' : ''}
            data-testid={`input-config-${field.key}`}
          />
          {field.unit === '%' && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
          )}
        </div>
        {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
      </div>
    );
  }

  if (field.type === 'select') {
    return (
      <div className="space-y-2">
        <Label>{field.label}</Label>
        <Select value={String(value)} onValueChange={onChange}>
          <SelectTrigger data-testid={`select-config-${field.key}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {field.options?.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
      </div>
    );
  }

  if (field.type === 'multiselect') {
    const selectedValues: string[] = Array.isArray(value) ? value : [];
    return (
      <div className="space-y-2">
        <Label>{field.label}</Label>
        <div className="flex flex-wrap gap-2">
          {field.options?.map(opt => {
            const isSelected = selectedValues.includes(opt.value);
            return (
              <Badge
                key={opt.value}
                variant={isSelected ? 'default' : 'outline'}
                className="cursor-pointer toggle-elevate"
                onClick={() => {
                  if (isSelected) {
                    onChange(selectedValues.filter(v => v !== opt.value));
                  } else {
                    onChange([...selectedValues, opt.value]);
                  }
                }}
                data-testid={`badge-day-${opt.value}`}
              >
                {opt.label}
              </Badge>
            );
          })}
        </div>
        {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
      </div>
    );
  }

  if (field.type === 'text') {
    return (
      <div className="space-y-2">
        <Label>{field.label}</Label>
        <Input
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          placeholder={field.helpText || ''}
          data-testid={`input-config-${field.key}`}
        />
        {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
      </div>
    );
  }

  return null;
}

function CampaignCard({
  campaign,
  templates,
  onStatusAction,
  formatCents,
  getStatusBadge,
}: {
  campaign: Campaign;
  templates: CampaignTemplate[];
  onStatusAction: (id: string, action: string) => void;
  formatCents: (cents: number) => string;
  getStatusBadge: (status: string) => JSX.Element;
}) {
  const template = templates.find(t => t.type === campaign.templateType);
  const Icon = template ? (ICON_MAP[template.icon] || Target) : Target;
  const color = template?.color || '#666';

  const budgetPercent = campaign.budgetCents
    ? Math.min(100, Math.round((campaign.spentCents / campaign.budgetCents) * 100))
    : null;

  const startDate = campaign.startDate ? new Date(campaign.startDate).toLocaleDateString() : '—';
  const endDate = campaign.endDate ? new Date(campaign.endDate).toLocaleDateString() : '—';
  const daysRemaining = campaign.endDate
    ? Math.max(0, Math.ceil((new Date(campaign.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <Card data-testid={`campaign-card-${campaign.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <div
              className="flex-shrink-0 p-2 rounded-md"
              style={{ backgroundColor: `${color}15` }}
            >
              <Icon className="w-5 h-5" style={{ color }} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-sm">{campaign.name}</h3>
                {getStatusBadge(campaign.status)}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {template?.name || campaign.templateType}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {campaign.status === 'active' && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onStatusAction(campaign.id, 'paused')}
                  title="Pause"
                >
                  <Pause className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onStatusAction(campaign.id, 'completed')}
                  title="Complete"
                >
                  <Square className="w-4 h-4" />
                </Button>
              </>
            )}
            {campaign.status === 'paused' && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onStatusAction(campaign.id, 'active')}
                  title="Resume"
                >
                  <Play className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onStatusAction(campaign.id, 'delete')}
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
            {(campaign.status === 'completed' || campaign.status === 'expired' || campaign.status === 'draft') && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onStatusAction(campaign.id, 'delete')}
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          <div>
            <p className="text-xs text-muted-foreground">Duration</p>
            <p className="text-sm font-medium">{startDate} – {endDate}</p>
            {daysRemaining !== null && campaign.status === 'active' && (
              <p className="text-xs text-muted-foreground">{daysRemaining} days left</p>
            )}
          </div>
          {campaign.budgetCents && (
            <div>
              <p className="text-xs text-muted-foreground">Budget</p>
              <p className="text-sm font-medium">
                {formatCents(campaign.spentCents)} / {formatCents(campaign.budgetCents)}
              </p>
              <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${budgetPercent}%`,
                    backgroundColor: (budgetPercent || 0) > 80 ? '#ef4444' : color,
                  }}
                />
              </div>
            </div>
          )}
          <div>
            <p className="text-xs text-muted-foreground">Customers Reached</p>
            <p className="text-sm font-medium">{(campaign.metrics as any)?.customersReached || 0}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Incentives Issued</p>
            <p className="text-sm font-medium">
              {((campaign.metrics as any)?.couponsIssued || 0) + ((campaign.metrics as any)?.kudozAwarded || 0)}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
